import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  createIntegration,
  deleteIntegration,
  getAllFacebookPages,
  getAllIntegrations,
  getAllStudentLeads,
  getIntegrationByID,
  getLeadFormByPageID,
  updateIntegration,
  updatePageSubscription,
} from "./services/integration.services";
import { ILeadIntegrationDetails, ILeadState } from "./integrationModel";
import { ErrorToast } from "../../components/toast/Toasts";

const initialState: ILeadState = {
  loading: false,
  error: "",
  IntegrationID: null,
  FBPageList: [],
  LeadFormList: [],
  LeadIntegrationList: [],
  LeadIntegrationDetails: {
    Title: "",
    FormID: "",
    PageID: "",
  },
  StudentLeadList: [],
};

const integrationSlice = createSlice({
  name: "integration",
  initialState,
  reducers: {
    setIntegrationID: (state, action) => {
      state.IntegrationID = action.payload;
    },
    clearIntegrationID: (state) => {
      state.IntegrationID = initialState.IntegrationID;
    },
    clearFBPageList: (state) => {
      state.FBPageList = initialState.FBPageList;
    },
    clearLeadFormList: (state) => {
      state.LeadFormList = initialState.LeadFormList;
    },
    clearLeadIntegrationDetails: (state) => {
      state.LeadIntegrationDetails = initialState.LeadIntegrationDetails;
    },
    clearStudentLeadList: (state) => {
      state.StudentLeadList = initialState.StudentLeadList;
    },
  },
  extraReducers: (builder) => {
    // Fetch Facebook Page List
    builder.addCase(getAllFacebookPages.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      getAllFacebookPages.fulfilled,
      (state, action: PayloadAction<any[]>) => {
        state.loading = false;
        state.FBPageList = action.payload;
        state.error = "";
      }
    );
    builder.addCase(getAllFacebookPages.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.FBPageList = [];
    });

    // Fetch Facebook Lead Form List By Page ID
    builder.addCase(getLeadFormByPageID.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      getLeadFormByPageID.fulfilled,
      (state, action: PayloadAction<any[]>) => {
        state.loading = false;
        state.LeadFormList = action.payload;
        state.error = "";
      }
    );
    builder.addCase(getLeadFormByPageID.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.LeadFormList = [];
    });

    // Integration Section
    builder.addCase(getAllIntegrations.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      getAllIntegrations.fulfilled,
      (state, action: PayloadAction<any[]>) => {
        state.loading = false;
        state.LeadIntegrationList = action.payload;
        state.error = "";
      }
    );
    builder.addCase(getAllIntegrations.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.LeadIntegrationList = [];
    });

    builder.addCase(createIntegration.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createIntegration.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(createIntegration.rejected, (state) => {
      state.loading = false;
      state.error = "Something went wrong";
      ErrorToast(state.error);
    });

    builder.addCase(updateIntegration.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateIntegration.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(updateIntegration.rejected, (state) => {
      state.loading = false;
      state.error = "Something went wrong";
      ErrorToast(state.error);
    });

    builder.addCase(getIntegrationByID.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      getIntegrationByID.fulfilled,
      (state, action: PayloadAction<ILeadIntegrationDetails>) => {
        state.loading = false;
        state.LeadIntegrationDetails = action.payload;
      }
    );
    builder.addCase(getIntegrationByID.rejected, (state) => {
      state.loading = false;
      state.error = "Something went wrong";
    });

    builder.addCase(deleteIntegration.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deleteIntegration.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(deleteIntegration.rejected, (state) => {
      state.loading = false;
      state.error = "Something went wrong";
      ErrorToast(state.error);
    });

    builder.addCase(getAllStudentLeads.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      getAllStudentLeads.fulfilled,
      (state, action: PayloadAction<any[]>) => {
        state.loading = false;
        state.StudentLeadList = action.payload;
        state.error = "";
      }
    );
    builder.addCase(getAllStudentLeads.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.StudentLeadList = [];
    });

    builder.addCase(updatePageSubscription.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updatePageSubscription.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(updatePageSubscription.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });
  },
});

export default integrationSlice.reducer;
export const {
  setIntegrationID,
  clearIntegrationID,
  clearFBPageList,
  clearLeadFormList,
  clearLeadIntegrationDetails,
  clearStudentLeadList,
} = integrationSlice.actions;
